/**
 * Phase 8.3 — QuestionVisual: pure-SVG renderer for question stimuli.
 *
 * Supported visual_type values (matching backend):
 *   table, bar_chart, line_graph, pie_chart, coordinate_grid,
 *   geometry_diagram, map, pattern_grid, number_grid, image, none.
 *
 * Renders responsively: SVG has viewBox 0..100 (or explicit) and scales to
 * fit its container width. Mobile-friendly by design.
 */
import React from 'react';

/* ------------- primitives ------------- */
const Frame = ({ children, ratio = '4 / 3', className = '', dataTestid }) => (
  <div className={`w-full rounded-xl border border-gray-200 bg-white p-3 overflow-hidden ${className}`} data-testid={dataTestid}>
    <div style={{ aspectRatio: ratio }} className="w-full">{children}</div>
  </div>
);
const Caption = ({ text }) => text ? (
  <div className="mt-1.5 text-[11px] text-gray-500 italic">{text}</div>
) : null;

/* ------------- Table ------------- */
const TableVisual = ({ payload }) => {
  const headers = payload?.headers || [];
  const rows = payload?.rows || [];
  return (
    <div className="w-full overflow-x-auto rounded-xl border border-gray-200 bg-white" data-testid="qv-table">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>{headers.map((h, i) => <th key={i} className="text-left px-3 py-2 font-bold text-gray-800 border-b border-gray-100">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className={ri === rows.length - 1 ? 'bg-red-50 font-bold' : ''}>
              {r.map((c, ci) => <td key={ci} className="px-3 py-2 text-gray-800 border-b border-gray-50">{String(c)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ------------- Bar chart ------------- */
const BarChart = ({ payload }) => {
  const labels = payload?.labels || [];
  const values = payload?.values || [];
  const color = payload?.color || '#DC2626';
  const max = Math.max(...values, 1);
  const bw = 60 / Math.max(labels.length, 1); // bar width in svg units
  return (
    <Frame ratio="4 / 3" dataTestid="qv-bar_chart">
      <svg viewBox="0 0 100 80" preserveAspectRatio="xMidYMid meet" className="w-full h-full">
        {/* axes */}
        <line x1="12" y1="10" x2="12" y2="65" stroke="#374151" strokeWidth="0.5" />
        <line x1="12" y1="65" x2="92" y2="65" stroke="#374151" strokeWidth="0.5" />
        {/* y-title */}
        {payload?.y_title && <text x="4" y="35" fontSize="4" fill="#4B5563" transform="rotate(-90 4 35)">{payload.y_title}</text>}
        {/* bars */}
        {values.map((v, i) => {
          const h = (v / max) * 50;
          const x = 15 + i * (bw + 2);
          return (
            <g key={i}>
              <rect x={x} y={65 - h} width={bw} height={h} fill={color} rx="0.6" />
              <text x={x + bw / 2} y={72} fontSize="3.5" fill="#111827" textAnchor="middle">{labels[i]}</text>
              <text x={x + bw / 2} y={64 - h} fontSize="3.2" fill="#374151" textAnchor="middle">{v}</text>
            </g>
          );
        })}
        {payload?.x_title && <text x="52" y="78" fontSize="4" fill="#4B5563" textAnchor="middle">{payload.x_title}</text>}
      </svg>
    </Frame>
  );
};

/* ------------- Line graph ------------- */
const LineGraph = ({ payload }) => {
  const xLabels = payload?.x_labels || [];
  const series = payload?.series || [];
  const allY = series.flatMap(s => s.y || []);
  const maxY = Math.max(...allY, 1);
  const minY = Math.min(...allY, 0);
  const xstep = 80 / Math.max(xLabels.length - 1, 1);
  const yScale = (y) => 65 - ((y - minY) / (maxY - minY || 1)) * 55;
  return (
    <Frame ratio="4 / 3" dataTestid="qv-line_graph">
      <svg viewBox="0 0 100 80" className="w-full h-full">
        <line x1="12" y1="10" x2="12" y2="65" stroke="#374151" strokeWidth="0.5" />
        <line x1="12" y1="65" x2="92" y2="65" stroke="#374151" strokeWidth="0.5" />
        {series.map((s, si) => {
          const pts = (s.y || []).map((v, i) => `${12 + i * xstep},${yScale(v)}`).join(' ');
          return (
            <g key={si}>
              <polyline points={pts} fill="none" stroke={s.color || '#DC2626'} strokeWidth="1" />
              {(s.y || []).map((v, i) => <circle key={i} cx={12 + i * xstep} cy={yScale(v)} r="1" fill={s.color || '#DC2626'} />)}
            </g>
          );
        })}
        {xLabels.map((lbl, i) => (
          <text key={i} x={12 + i * xstep} y="72" fontSize="3.2" fill="#111827" textAnchor="middle">{lbl}</text>
        ))}
        {payload?.y_title && <text x="4" y="35" fontSize="4" fill="#4B5563" transform="rotate(-90 4 35)">{payload.y_title}</text>}
        {payload?.x_title && <text x="52" y="78" fontSize="4" fill="#4B5563" textAnchor="middle">{payload.x_title}</text>}
      </svg>
    </Frame>
  );
};

/* ------------- Pie chart ------------- */
const PieChart = ({ payload }) => {
  const slices = payload?.slices || [];
  const total = slices.reduce((a, s) => a + (s.value || 0), 0) || 1;
  let cum = 0;
  const cx = 40, cy = 40, r = 28;
  const arcs = slices.map((s, i) => {
    const a0 = (cum / total) * Math.PI * 2 - Math.PI / 2;
    cum += s.value || 0;
    const a1 = (cum / total) * Math.PI * 2 - Math.PI / 2;
    const large = (a1 - a0) > Math.PI ? 1 : 0;
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const path = `M${cx},${cy} L${x0},${y0} A${r},${r} 0 ${large} 1 ${x1},${y1} Z`;
    return { path, color: s.color || '#DC2626', label: s.label, value: s.value, mid: [(a0 + a1) / 2] };
  });
  return (
    <Frame ratio="4 / 3" dataTestid="qv-pie_chart">
      <svg viewBox="0 0 100 80" className="w-full h-full">
        {arcs.map((a, i) => <path key={i} d={a.path} fill={a.color} stroke="#fff" strokeWidth="0.5" />)}
        {/* legend */}
        {slices.map((s, i) => (
          <g key={i}>
            <rect x="72" y={10 + i * 6} width="4" height="4" fill={s.color || '#DC2626'} />
            <text x="78" y={14 + i * 6} fontSize="3.5" fill="#111827">{s.label} — {Math.round(((s.value || 0) / total) * 100)}%</text>
          </g>
        ))}
      </svg>
    </Frame>
  );
};

/* ------------- Coordinate grid ------------- */
const CoordinateGrid = ({ payload }) => {
  const xMin = payload?.x_min ?? 0, xMax = payload?.x_max ?? 10;
  const yMin = payload?.y_min ?? 0, yMax = payload?.y_max ?? 10;
  const sx = (x) => 15 + ((x - xMin) / (xMax - xMin)) * 70;
  const sy = (y) => 65 - ((y - yMin) / (yMax - yMin)) * 55;
  const xs = [], ys = [];
  for (let i = xMin; i <= xMax; i++) xs.push(i);
  for (let j = yMin; j <= yMax; j++) ys.push(j);
  const poly = (payload?.polygon || []).map(([x, y]) => `${sx(x)},${sy(y)}`).join(' ');
  return (
    <Frame ratio="4 / 3" dataTestid="qv-coordinate_grid">
      <svg viewBox="0 0 100 80" className="w-full h-full">
        {/* gridlines */}
        {xs.map((x, i) => <line key={`vx${i}`} x1={sx(x)} y1={sy(yMin)} x2={sx(x)} y2={sy(yMax)} stroke="#E5E7EB" strokeWidth="0.3" />)}
        {ys.map((y, i) => <line key={`hy${i}`} x1={sx(xMin)} y1={sy(y)} x2={sx(xMax)} y2={sy(y)} stroke="#E5E7EB" strokeWidth="0.3" />)}
        {/* axes */}
        <line x1={sx(xMin)} y1={sy(0)} x2={sx(xMax)} y2={sy(0)} stroke="#111827" strokeWidth="0.5" />
        <line x1={sx(0)} y1={sy(yMin)} x2={sx(0)} y2={sy(yMax)} stroke="#111827" strokeWidth="0.5" />
        {/* polygon */}
        {poly && <polygon points={poly} fill="rgba(220,38,38,0.15)" stroke="#DC2626" strokeWidth="0.6" />}
        {/* points */}
        {(payload?.points || []).map((p, i) => (
          <g key={i}>
            <circle cx={sx(p.x)} cy={sy(p.y)} r="1.2" fill="#DC2626" />
            <text x={sx(p.x) + 2} y={sy(p.y) - 1} fontSize="3.5" fill="#111827">{p.label} ({p.x},{p.y})</text>
          </g>
        ))}
        {/* axis labels */}
        {xs.map((x, i) => i % Math.ceil(xs.length / 6) === 0 ? <text key={`lx${i}`} x={sx(x)} y={sy(0) + 4} fontSize="3" fill="#4B5563" textAnchor="middle">{x}</text> : null)}
        {ys.map((y, i) => i % Math.ceil(ys.length / 6) === 0 ? <text key={`ly${i}`} x={sx(0) - 3} y={sy(y) + 1} fontSize="3" fill="#4B5563" textAnchor="end">{y}</text> : null)}
      </svg>
    </Frame>
  );
};

/* ------------- Geometry diagram ------------- */
const GeometryDiagram = ({ payload }) => {
  const shape = payload?.shape || 'triangle';
  return (
    <Frame ratio="1 / 1" dataTestid="qv-geometry_diagram">
      <svg viewBox="0 0 100 100" className="w-full h-full">
        {shape === 'circle' && (
          <circle cx={payload.center?.[0] || 50} cy={payload.center?.[1] || 50} r={payload.radius || 30} fill="rgba(220,38,38,0.08)" stroke="#DC2626" strokeWidth="0.8" />
        )}
        {(shape === 'triangle' || shape === 'rectangle' || shape === 'polygon') && payload.vertices && (
          <polygon points={payload.vertices.map(v => v.join(',')).join(' ')} fill="rgba(17,24,39,0.05)" stroke="#111827" strokeWidth="0.8" />
        )}
        {/* sides */}
        {(payload?.sides || []).map((s, i) => {
          const mx = (s.a[0] + s.b[0]) / 2, my = (s.a[1] + s.b[1]) / 2;
          return (
            <g key={`s${i}`}>
              <line x1={s.a[0]} y1={s.a[1]} x2={s.b[0]} y2={s.b[1]} stroke="#DC2626" strokeWidth="0.6" strokeDasharray="0.5 0.5" />
              <text x={mx + 2} y={my - 1} fontSize="4" fill="#7F1D1D" fontWeight="bold">{s.text}</text>
            </g>
          );
        })}
        {/* angles */}
        {(payload?.angles || []).map((a, i) => (
          <text key={`a${i}`} x={a.at[0]} y={a.at[1]} fontSize="4" fill="#111827" fontWeight="bold" textAnchor="middle">{a.value}</text>
        ))}
        {/* labels */}
        {(payload?.labels || []).map((l, i) => (
          <text key={`l${i}`} x={l.at[0]} y={l.at[1]} fontSize="4" fill="#111827" textAnchor="middle" fontWeight="bold">{l.text}</text>
        ))}
      </svg>
    </Frame>
  );
};

/* ------------- Map (simplified) ------------- */
const MapVisual = ({ payload }) => (
  <Frame ratio="4 / 3" dataTestid="qv-map">
    <svg viewBox="0 0 100 80" className="w-full h-full">
      <rect x="0" y="0" width="100" height="80" fill="#F0FDF4" />
      {/* Compass */}
      {payload?.north && (
        <g transform="translate(90 12)">
          <circle r="6" fill="#fff" stroke="#111827" strokeWidth="0.4" />
          <text x="0" y="-1.5" fontSize="4" fill="#111827" textAnchor="middle" fontWeight="bold">N</text>
          <path d="M0,-3 L1.5,3 L-1.5,3 Z" fill="#DC2626" />
        </g>
      )}
      {/* Roads */}
      {(payload?.roads || []).map((r, i) => (
        <line key={`r${i}`} x1={r[0][0]} y1={r[0][1]} x2={r[1][0]} y2={r[1][1]} stroke="#9CA3AF" strokeWidth="1.2" strokeLinecap="round" />
      ))}
      {(payload?.nodes || []).map((n, i) => (
        <g key={`n${i}`}>
          <circle cx={n.x} cy={n.y} r="3" fill={n.color || '#DC2626'} />
          <text x={n.x} y={n.y - 5} fontSize="3.8" fill="#111827" textAnchor="middle" fontWeight="bold">{n.label}</text>
        </g>
      ))}
    </svg>
  </Frame>
);

/* ------------- Pattern grid ------------- */
const PatternGrid = ({ payload }) => {
  const cells = payload?.cells || [];
  const cols = payload?.columns || 3;
  return (
    <div className="w-full rounded-xl border border-gray-200 bg-white p-3" data-testid="qv-pattern_grid">
      <div className="grid gap-2 mx-auto max-w-xs" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {cells.map((c, i) => (
          <div key={i} className="aspect-square bg-gray-50 rounded-lg flex items-center justify-center">
            {c.shape === '?' && <span className="text-2xl font-bold text-gray-400">?</span>}
            {c.shape === 'circle' && <div className="w-3/5 h-3/5 rounded-full" style={{ background: c.color }} />}
            {c.shape === 'square' && <div className="w-3/5 h-3/5" style={{ background: c.color }} />}
            {c.shape === 'triangle' && (
              <svg viewBox="0 0 10 10" className="w-3/5 h-3/5"><polygon points="5,1 9,9 1,9" fill={c.color} /></svg>
            )}
            {c.shape === 'star' && (
              <svg viewBox="0 0 24 24" className="w-3/5 h-3/5"><polygon points="12,2 15,9 22,9 17,14 19,22 12,17 5,22 7,14 2,9 9,9" fill={c.color} /></svg>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------- Number grid ------------- */
const NumberGrid = ({ payload }) => {
  const rows = payload?.rows || [];
  const highlight = new Set((payload?.highlight || []).map(([r, c]) => `${r}-${c}`));
  return (
    <div className="w-full rounded-xl border border-gray-200 bg-white p-3" data-testid="qv-number_grid">
      <div className="inline-grid gap-0 mx-auto" style={{ gridTemplateColumns: `repeat(${rows[0]?.length || 3}, minmax(0, 1fr))` }}>
        {rows.flatMap((row, ri) => row.map((v, ci) => (
          <div key={`${ri}-${ci}`}
            className={`w-14 h-14 flex items-center justify-center text-xl font-bold border border-gray-200 ${highlight.has(`${ri}-${ci}`) ? 'bg-amber-100 text-amber-800' : 'bg-white text-gray-900'}`}>
            {String(v)}
          </div>
        )))}
      </div>
    </div>
  );
};

/* ------------- Image ------------- */
const ImageVisual = ({ payload, altText }) => (
  <div className="w-full rounded-xl border border-gray-200 bg-white p-2 overflow-hidden" data-testid="qv-image">
    {payload?.image_url
      ? <img src={payload.image_url} alt={altText || 'Question diagram'} className="w-full h-auto rounded-lg max-h-[420px] object-contain mx-auto" />
      : <div className="text-xs text-gray-400 italic p-4 text-center">No image attached.</div>}
  </div>
);

/* ------------- Public entry point ------------- */
const QuestionVisual = ({ visual_type, visual_payload, visual_caption, visual_alt_text }) => {
  if (!visual_type || visual_type === 'none') return null;
  const props = { payload: visual_payload || {}, altText: visual_alt_text };
  let body;
  switch (visual_type) {
    case 'table': body = <TableVisual {...props} />; break;
    case 'bar_chart': body = <BarChart {...props} />; break;
    case 'line_graph': body = <LineGraph {...props} />; break;
    case 'pie_chart': body = <PieChart {...props} />; break;
    case 'coordinate_grid': body = <CoordinateGrid {...props} />; break;
    case 'geometry_diagram': body = <GeometryDiagram {...props} />; break;
    case 'map': body = <MapVisual {...props} />; break;
    case 'pattern_grid': body = <PatternGrid {...props} />; break;
    case 'number_grid': body = <NumberGrid {...props} />; break;
    case 'image': body = <ImageVisual {...props} />; break;
    default: return null;
  }
  return (
    <div className="mb-2" role="img" aria-label={visual_alt_text || visual_caption || visual_type} data-testid={`question-visual-${visual_type}`}>
      {body}
      <Caption text={visual_caption} />
    </div>
  );
};

export default QuestionVisual;
